/**
 * PreSet AGM with Audited Financial Statements — 문서 준비 payload.
 */
import { classifyAgmAfsDoc } from './agmAfsDocVariableMap';
import {
  buildAgmAfsAllDirectorsSignerVariables,
  buildAgmAfsAttendanceSignerVariables,
  buildAgmAfsConsentSignerVariables,
  buildAgmAfsMinutesMultipleChairmanSignerVariables,
  buildAgmAfsMinutesSoleCorporateSignerVariables,
  buildAgmAfsMinutesSoleIndividualSignerVariables,
} from './agmAfsSigners';
import {
  toAgmAfsDirectorsResolutionOnlyVariables,
  type AgmAfsFormModel,
} from './useFormPayload';
import { buildSgPresetPrepareDocumentItem } from '../../../presetDocCustSave';

export interface SelectionDocRow {
  tmpId?: number | null;
  templateName?: string;
}

const CONSENT_AGREEMENT = /177|203|agreement.*section|consent\s*to\s*short|short\s*notice/i;
const MINUTES_MULTIPLE = /minutes.*multiple/i;
const MINUTES_SOLE_CORPORATE = /minutes.*sole\s*corporate|representative|holding\s*company/i;
const MINUTES_SOLE_INDIVIDUAL = /minutes.*sole\s*individual/i;

type MemberType = 'multiple' | 'sole_corporate' | 'sole_individual';

function shouldIncludeDoc(
  templateName: string,
  isShortNotice: boolean,
  memberType: MemberType,
): boolean {
  if (CONSENT_AGREEMENT.test(templateName)) return isShortNotice;
  if (MINUTES_MULTIPLE.test(templateName)) return memberType === 'multiple';
  if (MINUTES_SOLE_CORPORATE.test(templateName)) return memberType === 'sole_corporate';
  if (MINUTES_SOLE_INDIVIDUAL.test(templateName)) return memberType === 'sole_individual';
  return true;
}

export function buildAgmAfsPresetPrepareDocuments(input: {
  selectionDocuments: SelectionDocRow[];
  commonFormData: Record<string, unknown>;
  formModel: AgmAfsFormModel;
  personName: string;
  entityCd?: string;
  isShortNotice: boolean;
  memberType: MemberType;
}) {
  const drOnlyVars = toAgmAfsDirectorsResolutionOnlyVariables(input.formModel);
  const allDirectorSigners = buildAgmAfsAllDirectorsSignerVariables(input.formModel);

  return (input.selectionDocuments || [])
    .filter((d) => d.tmpId)
    .filter((d) =>
      shouldIncludeDoc(d.templateName || '', input.isShortNotice, input.memberType),
    )
    .map((d) => {
      const templateName = d.templateName || '';
      const docKind = classifyAgmAfsDoc(templateName);
      let commonFormData = { ...input.commonFormData };

      if (docKind === 'directors_resolution') {
        const drSigners = input.formModel.specifySigners
          ? drOnlyVars
          : { ...drOnlyVars, ...(allDirectorSigners || {}) };
        commonFormData = { ...commonFormData, ...drSigners };
      } else if (docKind === 'attendance') {
        const v = buildAgmAfsAttendanceSignerVariables(input.formModel);
        if (v) commonFormData = { ...commonFormData, ...v };
      } else if (docKind === 'consent') {
        const v = buildAgmAfsConsentSignerVariables(input.formModel);
        if (v) commonFormData = { ...commonFormData, ...v };
      } else if (docKind === 'minutes_multiple') {
        const v = buildAgmAfsMinutesMultipleChairmanSignerVariables(input.formModel);
        if (v) commonFormData = { ...commonFormData, ...v };
      } else if (docKind === 'minutes_sole_corporate') {
        const v = buildAgmAfsMinutesSoleCorporateSignerVariables(input.formModel);
        if (v) commonFormData = { ...commonFormData, ...v };
      } else if (docKind === 'minutes_sole_individual') {
        const v = buildAgmAfsMinutesSoleIndividualSignerVariables(input.formModel);
        if (v) commonFormData = { ...commonFormData, ...v };
      }
      // Notice of AGM — Signers는 PreSet 생성 시 자동 주입하지 않음 (Detail EDIT에서 수동 지정)

      return buildSgPresetPrepareDocumentItem({
        templateId: d.tmpId as number | string,
        templateName,
        personName: input.personName,
        commonFormData,
        entityCd: input.entityCd,
      });
    });
}
